import get from "lodash/get";
import {cloneElement, ReactElement, ReactNode} from "react";
import {useFormContext} from "react-hook-form";
import {ActionMeta, GroupBase, MultiValue, PropsValue, SelectComponentsConfig} from "react-select";

import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectOption, SelectProps} from "@/components/select/Select.types.ts";
import {getFormError} from "@/lib/form/form.utils.ts";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";

export type SelectFieldBaseProps<T = string> = Omit<FieldProps, "helpText"> & {
  isMulti?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  helpText?: string | ReactNode;
  clearErrorsOnChange?: boolean;
  components?: SelectComponentsConfig<SelectOption<T>, boolean, GroupBase<SelectOption<T>>>;
  menuPortalTarget?: HTMLElement;
  menuPosition?: "absolute" | "fixed";
  maxSelections?: number;
};

export type SelectFieldProps<T> =
  | (SelectFieldBaseProps<T> & {select?: undefined; isMulti?: false} & {
      options?: SelectOption<T>[];
      onChange?: (value: T | undefined, actionMeta: ActionMeta<SelectOption<T>>) => void;
    })
  | (SelectFieldBaseProps<T> & {select?: undefined; isMulti: true} & {
      options?: SelectOption<T>[];
      onChange?: (value: T[] | undefined, actionMeta: ActionMeta<SelectOption<T>>) => void;
    })
  | (SelectFieldBaseProps<T> & {select: ReactElement<SelectProps<T>>} & {
      options?: never;
      onChange?: never;
    });

export const SelectField = <T,>({
  options = [],
  className,
  label,
  name,
  isMulti,
  isClearable,
  isSearchable,
  tooltip,
  helpText,
  supportLink,
  required,
  hasRequiredLabel = false,
  disabled,
  placeholder,
  select,
  clearErrorsOnChange,
  onChange,
  autoFocus,
  components,
  menuPortalTarget,
  menuPosition,
  invalid,
  maxSelections,
}: SelectFieldProps<T>) => {
  const {
    watch,
    setValue,
    clearErrors,
    formState: {errors, defaultValues},
  } = useFormContext();

  const fieldError = getFormError(name, errors);

  const value = watch(name);
  const defaultValue = get(defaultValues, name);

  const selectedCount = Array.isArray(value) ? value.length : 0;
  const aboveMax = isMulti && maxSelections != null ? selectedCount >= maxSelections : undefined;

  const findSelected = (selected: unknown): PropsValue<SelectOption<T>> | undefined =>
    isMulti
      ? options.filter((o: SelectOption<T>) => Array.isArray(selected) && selected.includes(o.value))
      : options.find((o: SelectOption<T>) => o.value === selected);

  const commonProps = {
    id: name,
    inputId: `select-${name}`,
    name,
    isClearable,
    placeholder,
    isDisabled: disabled,
    isSearchable,
    "aria-invalid": !!fieldError || !!invalid,
    autoFocus,
    menuPortalTarget,
    menuPosition,
    maxSelections,
  };

  return (
    <div className={className}>
      {label && (
        <FormLabel helpText={tooltip} forId={`select-${name}`} required={required} hasRequiredLabel={hasRequiredLabel}>
          {label}
        </FormLabel>
      )}
      <>
        {select ? (
          cloneElement(select, {
            ...commonProps,
            ...select.props,
            onChange: (v: T | null, actionMeta: ActionMeta<T>) => {
              select.props.onChange?.(v, actionMeta);
              if (clearErrorsOnChange && select.props.value) {
                clearErrors(name);
              }
            },
          })
        ) : (
          <Select<SelectOption<T>, boolean>
            {...commonProps}
            defaultValue={findSelected(defaultValue)}
            onChange={(v, actionMeta) => {
              const shouldClearErrors = clearErrorsOnChange && value;
              const newValue = isMulti
                ? (v as MultiValue<SelectOption<T>> | null)?.map((option) => option.value)
                : (v as SelectOption<T> | null)?.value;
              setValue(name, newValue, {shouldDirty: true, shouldValidate: !shouldClearErrors});
              if (shouldClearErrors) {
                clearErrors(name);
              }
              if (isMulti) {
                onChange?.(newValue as T[] | undefined, actionMeta);
              } else {
                onChange?.(newValue as T | undefined, actionMeta);
              }
            }}
            value={findSelected(value)}
            options={options}
            isClearable={isClearable}
            isMulti={isMulti}
            components={components}
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
          />
        )}
      </>
      <FormSubtext
        error={fieldError?.toString()}
        supportLink={supportLink}
        subText={helpText}
        warning={aboveMax ? "Max. selected" : undefined}
      />
    </div>
  );
};
